import { useRef, useState } from 'react';
import { ImagePlus, Trash2 } from 'lucide-react';
import { Button, Field, cx } from './ui';

/**
 * Redimensiona y comprime una imagen en el navegador antes de guardarla.
 *
 * La imagen termina como data URL dentro de la base: no hay object storage
 * porque en Render se cobra aparte. Sin este paso, alguien sube la foto de 4 MB
 * que le sacó al logo con el celular y esa foto viaja después en **cada** carga
 * de la pantalla de ingreso de sus pacientes.
 *
 * El PNG y el WEBP conservan transparencia —un logo sobre fondo blanco forzado
 * se ve mal sobre cualquier color— y el SVG pasa tal cual: ya es chico y
 * redibujarlo en un canvas lo convertiría en mapa de bits.
 */
export async function shrinkImage(file: File, maxSide: number): Promise<string> {
  if (file.type === 'image/svg+xml') return readAsDataUrl(file);

  const dataUrl = await readAsDataUrl(file);
  const image = await loadImage(dataUrl);

  const scale = Math.min(1, maxSide / Math.max(image.width, image.height));
  const width = Math.round(image.width * scale);
  const height = Math.round(image.height * scale);

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;

  const context = canvas.getContext('2d');
  if (!context) return dataUrl;

  context.drawImage(image, 0, 0, width, height);

  // WEBP con transparencia y buena compresión. Si el navegador no lo soporta,
  // `toDataURL` devuelve PNG y sigue funcionando igual.
  return canvas.toDataURL('image/webp', 0.9);
}

function readAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(new Error('No pudimos leer el archivo'));
    reader.readAsDataURL(file);
  });
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('El archivo no es una imagen válida'));
    image.src = src;
  });
}

export function ImageUpload({
  label,
  hint,
  value,
  onChange,
  maxSide = 512,
  /** Fondo del recuadro de vista previa: un logo claro necesita fondo oscuro. */
  dark,
  round,
  readOnly,
}: {
  label: string;
  hint?: string;
  value: string | null;
  onChange: (value: string | null) => void;
  maxSide?: number;
  dark?: boolean;
  round?: boolean;
  /** Solo se mira. Sin botones: "Cambiar" apagado igual invita a tocarlo. */
  readOnly?: boolean;
}) {
  const input = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [working, setWorking] = useState(false);

  const handleFile = async (file: File | undefined): Promise<void> => {
    if (!file) return;
    setError(null);
    setWorking(true);
    try {
      onChange(await shrinkImage(file, maxSide));
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'No pudimos procesar la imagen');
    } finally {
      setWorking(false);
      // Se limpia para que elegir el mismo archivo otra vez vuelva a disparar.
      if (input.current) input.current.value = '';
    }
  };

  return (
    <Field label={label} hint={hint} error={error ?? undefined}>
      <div className="flex items-center gap-3">
        <div
          className={cx(
            'size-20 shrink-0 grid place-items-center overflow-hidden border border-border',
            round ? 'rounded-full' : 'rounded-md',
            dark ? 'bg-fg' : 'bg-surface',
          )}
        >
          {value ? (
            <img src={value} alt="" className="max-h-full max-w-full object-contain" />
          ) : (
            <ImagePlus className="size-6 text-fg-subtle" />
          )}
        </div>

        {readOnly ? (
          <p className="text-sm text-fg-muted">{value ? 'Cargada' : 'Sin cargar'}</p>
        ) : (
        <div className="flex flex-col gap-2">
          <input
            ref={input}
            type="file"
            accept="image/png,image/jpeg,image/webp,image/svg+xml"
            className="hidden"
            onChange={(event) => void handleFile(event.target.files?.[0])}
          />
          <Button
            type="button"
            size="sm"
            variant="outline"
            loading={working}
            onClick={() => input.current?.click()}
          >
            {value ? 'Cambiar' : 'Subir imagen'}
          </Button>
          {value && (
            <Button type="button" size="sm" variant="ghost" onClick={() => onChange(null)}>
              <Trash2 className="size-4" />
              Quitar
            </Button>
          )}
        </div>
        )}
      </div>
    </Field>
  );
}
